import React from 'react';
import { Formik } from 'formik';
import {
    TextField,
    withStyles,
    Button,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
} from '@material-ui/core';
import * as Yup from 'yup';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: '100%',
        marginTop: '10px',
    },
    helperText: {
        marginTop: '4px',
    },
    formControl: {
        width: '100%',
    },
});

const SKUMappingForm = (props) => {
    const { classes, formSettings, initialValues, handleSubmit, translations } = props;

    const productTypes = [
        { value: 'PCProduct', label: translations['pc_product'] || 'PC Product' },
        { value: 'MobileProduct', label: translations['mobile_product'] || 'Mobile Product' },
        { value: 'StorageProduct', label: translations['storage_product'] || 'Storage Product' },
        { value: 'WatchProduct', label: translations['watch'] || 'Watch' },
        { value: 'HeadphoneProduct', label: translations['headphone'] || 'Headphone' },
    ];

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const {
                        values,
                        touched,
                        errors,
                        isValid,
                        dirty,
                        isSubmitting,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                    } = formikProps;

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={'h6'}>
                                {formSettings.FormTitle ? formSettings.FormTitle : null}
                            </Typography>
                            <FormControl className={classes.formControl} margin={'dense'}>
                                <InputLabel shrink>
                                    {translations['select_product_type'] || 'Please select product type'}
                                </InputLabel>
                                <Select
                                    name="ProductType"
                                    value={values.ProductType}
                                    onChange={handleChange}
                                    label={translations['product_type'] || 'Product type'}
                                    helperText={errors.ProductType}
                                    margin={'dense'}
                                    fullWidth
                                >
                                    {(productTypes ?? []).map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <TextField
                                name="Vendor"
                                value={values.Vendor}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Vendor && touched.Vendor)}
                                placeholder={translations['vendor'] || 'Vendor'}
                                label={translations['vendor'] || 'Vendor'}
                                helperText={errors.Vendor}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Model"
                                value={values.Model}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Model && touched.Model)}
                                placeholder={translations['model'] || 'Model'}
                                label={translations['model'] || 'Model'}
                                helperText={errors.Model}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="ProductIdentifier"
                                value={values.ProductIdentifier}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.ProductIdentifier && touched.ProductIdentifier)}
                                placeholder={translations['product_identifier'] || 'Product identifier'}
                                label={translations['product_identifier'] || 'Product identifier'}
                                helperText={errors.ProductIdentifier}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Storage"
                                value={values.Storage}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Storage && touched.Storage)}
                                placeholder={translations['storage'] || 'Storage'}
                                label={translations['storage'] || 'Storage'}
                                helperText={errors.Storage}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Memory"
                                value={values.Memory}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Memory && touched.Memory)}
                                placeholder={translations['memory'] || 'Memory'}
                                label={translations['memory'] || 'Memory'}
                                helperText={errors.Memory}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="CPU"
                                value={values.CPU}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.CPU && touched.CPU)}
                                placeholder={translations['cpu'] || 'CPU'}
                                label={translations['cpu'] || 'CPU'}
                                helperText={errors.CPU}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="GPU"
                                value={values.GPU}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.GPU && touched.GPU)}
                                placeholder={translations['gpu'] || 'GPU'}
                                label={translations['gpu'] || 'GPU'}
                                helperText={errors.GPU}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Grading"
                                value={values.Grading}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Grading && touched.Grading)}
                                placeholder={translations['grading'] || 'Grading'}
                                label={translations['grading'] || 'Grading'}
                                helperText={errors.Grading}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Color"
                                value={values.Color}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Color && touched.Color)}
                                placeholder={translations['color'] || 'Color'}
                                label={translations['color'] || 'Color'}
                                helperText={errors.Color}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="Carrier"
                                value={values.Carrier}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.Carrier && touched.Carrier)}
                                placeholder={translations['carrier'] || 'Carrier'}
                                label={translations['carrier'] || 'Carrier'}
                                helperText={errors.Carrier}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <TextField
                                name="SKU"
                                value={values.SKU}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={!!(errors.SKU && touched.SKU)}
                                placeholder={translations['sku'] || 'SKU'}
                                label={translations['sku'] || 'SKU'}
                                helperText={errors.SKU}
                                margin={'dense'}
                                fullWidth={true}
                            />
                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(SKUMappingForm);
